import { useCallback, useEffect, useRef, useState } from "react";
import {
  Sparkles,
} from "lucide-react";
import type { CSSProperties } from "react";
import "./VisitorPersona.css";

type ColorKey = "c1" | "c2" | "c3" | "c4" | "c5";

type Segment = {
  text: string;
  color?: ColorKey;
  chip?: string;
};

type Turn = {
  speaker: "visitor" | "smush";
  segments?: Segment[];
  plain?: string;
  reveals?: ColorKey[];
};

type PersonaRow = {
  key: string;
  value: string;
  source?: ColorKey;
  status: "captured" | "inferred" | "custom";
};

type LineData = {
  id: string;
  color: ColorKey;
  hex: string;
  d: string;
};

const LINE_CHANNEL_OFFSET: Record<ColorKey, number> = {
  c1: -128,
  c4: -76,
  c2: -24,
  c3: 32,
  c5: 86,
};

const FIELD_STYLE: Record<
  ColorKey,
  { hex: string; ink: string; soft: string; tint: string }
> = {
  c1: { hex: "#7357e8", ink: "#533db2", soft: "rgba(115, 87, 232, 0.08)", tint: "rgba(115, 87, 232, 0.04)" },
  c2: { hex: "#16845f", ink: "#0f6649", soft: "rgba(22, 132, 95, 0.08)", tint: "rgba(22, 132, 95, 0.04)" },
  c3: { hex: "#d14f45", ink: "#a13b34", soft: "rgba(209, 79, 69, 0.08)", tint: "rgba(209, 79, 69, 0.04)" },
  c4: { hex: "#2f6de0", ink: "#2454ad", soft: "rgba(47, 109, 224, 0.08)", tint: "rgba(47, 109, 224, 0.04)" },
  c5: { hex: "#c7771f", ink: "#925815", soft: "rgba(199, 119, 31, 0.08)", tint: "rgba(199, 119, 31, 0.04)" },
};

const turns: Turn[] = [
  {
    speaker: "visitor",
    reveals: ["c1"],
    segments: [
      { text: "I'm " },
      { text: "unable to get to the next module", color: "c1", chip: "Issue" },
      { text: " of my course. It just keeps loading." },
    ],
  },
  {
    speaker: "smush",
    plain: "I can help. Which course are you enrolled on?",
  },
  {
    speaker: "visitor",
    reveals: ["c2", "c4"],
    segments: [
      { text: "The " },
      { text: "120 Hour Advanced TEFL", color: "c2", chip: "Course" },
      { text: ". I'm based in the " },
      { text: "UK", color: "c4", chip: "Location" },
      { text: "." },
    ],
  },
  {
    speaker: "smush",
    plain: "Got it. Has this happened before?",
  },
  {
    speaker: "visitor",
    reveals: ["c3", "c5"],
    segments: [
      { text: "Honestly quite " },
      { text: "frustrated", color: "c3", chip: "Mood" },
      { text: " and stuck 20 mins. Just give me a " },
      { text: "concise", color: "c5", chip: "Style" },
      { text: " answer please." },
    ],
  },
];

const personaRows: PersonaRow[] = [
  {
    key: "Issue",
    value: "Unable to progress to the next module",
    source: "c1",
    status: "captured",
  },
  {
    key: "Location",
    value: "UK",
    source: "c4",
    status: "captured",
  },
  {
    key: "Course Enrolled",
    value: "120 Hour Advanced TEFL",
    source: "c2",
    status: "captured",
  },
  {
    key: "Emotional State",
    value: "Frustrated",
    source: "c3",
    status: "captured",
  },
  {
    key: "Certificate Type",
    value: "Advanced TEFL",
    source: "c2",
    status: "inferred",
  },
  {
    key: "Communication Style",
    value: "Concise",
    source: "c5",
    status: "captured",
  },
];

export default function VisitorPersona() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const coreRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Partial<Record<ColorKey, HTMLSpanElement | null>>>({});
  const rowRefs = useRef<Partial<Record<string, HTMLTableRowElement | null>>>({});
  const dotRefs = useRef<Partial<Record<string, HTMLSpanElement | null>>>({});
  const timersRef = useRef<number[]>([]);
  const startedRef = useRef(false);

  const [animated, setAnimated] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lines, setLines] = useState<LineData[]>([]);
  const [linesReady, setLinesReady] = useState(false);

  const visibleColors = new Set<ColorKey>();
  turns.forEach((turn, index) => {
    if (index <= activeIdx) {
      turn.reveals?.forEach((color) => visibleColors.add(color));
    }
  });

  // const visibleCount = visibleColors.size;
  const isComplete = activeIdx >= turns.length - 1;

  const calcLines = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    const coreRect = coreRef.current?.getBoundingClientRect();
    const hubX = coreRect
      ? coreRect.left + coreRect.width / 2 - canvasRect.left
      : canvasRect.width * 0.52;
    const next: LineData[] = [];
    personaRows.forEach((rowItem) => {
      const color = rowItem.source;
      if (!color) return;

      const chipEl = chipRefs.current[color];
      const dotEl = dotRefs.current[rowItem.key];
      if (!chipEl || !dotEl) return;

      const chip = chipEl.getBoundingClientRect();
      const dot = dotEl.getBoundingClientRect();
      const x1 = chip.right + 8 - canvasRect.left;
      const y1 = chip.top + chip.height / 2 - canvasRect.top;
      const x2 = dot.left + dot.width / 2 - canvasRect.left;
      const y2 = dot.top + dot.height / 2 - canvasRect.top;
      const channelX = hubX + LINE_CHANNEL_OFFSET[color];
      const d = [
        `M${x1},${y1}`,
        `L${channelX},${y1}`,
        `L${channelX},${y2}`,
        `L${x2},${y2}`,
      ].join(" ");

      next.push({ id: rowItem.key, color, hex: FIELD_STYLE[color].hex, d });
    });

    setLines(next);
    setLinesReady(true);
  }, []);

  const startAnimation = useCallback(() => {
    if (startedRef.current) return;

    startedRef.current = true;
    setAnimated(true);
    setActiveIdx(0);

    turns.slice(1).forEach((_, index) => {
      const turnIndex = index + 1;
      const timer = window.setTimeout(() => {
        setActiveIdx(turnIndex);
      }, index * 430 + 360);
      timersRef.current.push(timer);
    });

    const lineTimer = window.setTimeout(calcLines, turns.length * 430 + 460);
    timersRef.current.push(lineTimer);
  }, [calcLines]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) startAnimation();
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );

    observer.observe(section);

    const fallbackTimer = window.setTimeout(startAnimation, 650);

    return () => {
      observer.disconnect();
      window.clearTimeout(fallbackTimer);
      timersRef.current.forEach(window.clearTimeout);
      timersRef.current = [];
    };
  }, [startAnimation]);

  useEffect(() => {
    if (!animated) return;

    const frame = window.requestAnimationFrame(calcLines);
    const resizeObserver = new ResizeObserver(calcLines);
    if (canvasRef.current) resizeObserver.observe(canvasRef.current);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
    };
  }, [activeIdx, animated, calcLines]);

  return (
    <section className="vp-section" ref={sectionRef} id="visitor-persona">
      <div className="vp-container">
        <header className="vp-head">
          <div className="vp-head-left">
            <span className="vp-eyebrow">Visitor Intelligence</span>
            <h2 className="vp-heading">
              Read the room with visitor profile
            </h2>
          </div>
          <div className="vp-head-right">
            <p className="vp-sub">
              Smush reads chat signals like intent, mood, and urgency, then adapts the next response path in real time.
            </p>
          </div>
        </header>

        <div className={`vp-canvas${isComplete ? " vp-canvas--complete" : ""}`} ref={canvasRef}>
          <svg className="vp-svg" aria-hidden="true">
            <defs>
              {(Object.keys(FIELD_STYLE) as ColorKey[]).map((color) => (
                <linearGradient key={color} id={`vp-line-${color}`} x1="0%" x2="100%" y1="0%" y2="0%">
                  <stop offset="0%" stopColor={FIELD_STYLE[color].hex} stopOpacity="0.34" />
                  <stop offset="48%" stopColor={FIELD_STYLE[color].hex} stopOpacity="0.82" />
                  <stop offset="100%" stopColor={FIELD_STYLE[color].hex} stopOpacity="0.58" />
                </linearGradient>
              ))}
            </defs>
            {lines.map(({ id, color, d }) => (
              <path
                key={id}
                className="vp-line"
                d={d}
                stroke={`url(#vp-line-${color})`}
                style={{ opacity: linesReady && visibleColors.has(color) ? 1 : 0 }}
              />
            ))}
          </svg>

          <div className="vp-ambient vp-ambient-one" aria-hidden="true" />
          <div className="vp-ambient vp-ambient-two" aria-hidden="true" />

          <article className="vp-transcript" aria-label="Annotated live conversation">
            <div className="vp-pane-top">
              {/* <span className="vp-pane-label">Live transcript</span> */}
            </div>

            <div className="vp-thread">
              {turns.map((turn, index) => (
                <div
                  key={`${turn.speaker}-${index}`}
                  className={`vp-turn vp-turn--${turn.speaker}${index <= activeIdx ? " vp-turn--in" : ""}`}
                >
                  <span className="vp-speaker">
                    {turn.speaker === "visitor" ? "Visitor" : "Smush"}
                  </span>
                  {turn.speaker === "smush" ? (
                    <p className="vp-text vp-text--smush">
                      <span className="vp-smush-avatar" aria-hidden="true">
                        <Sparkles size={11} strokeWidth={2} />
                      </span>
                      <span>{turn.plain}</span>
                    </p>
                  ) : (
                    <p className="vp-text vp-text--visitor">
                      {turn.segments?.map((segment, segmentIndex) => {
                        if (!segment.color) {
                          return <span key={segmentIndex}>{segment.text}</span>;
                        }

                        const color = segment.color;
                        return (
                          <span key={segmentIndex} className="vp-annotated">
                            <span className={`vp-hl vp-hl--${color}`}>{segment.text}</span>
                            <span
                              className={`vp-chip vp-chip--${color}`}
                              ref={(el) => {
                                chipRefs.current[color] = el;
                              }}
                            >
                              {segment.chip}
                            </span>
                          </span>
                        );
                      })}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </article>

          <aside className="vp-intelligence" aria-label="Conversation intelligence engine">
            {/* <div className="vp-core-wrap" aria-hidden="true">
              <div className="vp-core-ring vp-core-ring-one" />
              <div className="vp-core-ring vp-core-ring-two" />
              <div className="vp-core" ref={coreRef}>
                <Sparkles size={18} strokeWidth={1.8} />
                // <strong>{visibleCount}/5</strong>
                <span>signals</span>
              </div>
            </div> */}

            <div className="vp-signal-stack">
              {personaRows.filter((row): row is PersonaRow & { source: ColorKey } => Boolean(row.source)).map((field) => {
                const style = FIELD_STYLE[field.source];
                return (
                  <span
                    key={`${field.key}-${field.source}`}
                    className={`vp-signal-pill${visibleColors.has(field.source) ? " vp-signal-pill--in" : ""}`}
                    style={{ "--vp-signal": style.hex, "--vp-signal-bg": style.soft } as CSSProperties}
                  >
                    {field.key}
                  </span>
                );
              })}
            </div>

          </aside>

          <article className="vp-profile" aria-label="Auto-built visitor persona table">
            <div className="vp-table-wrap">
              <table className="vp-persona-table">
                <tbody>
                  {personaRows.map((row) => {
                    const visible = row.source ? visibleColors.has(row.source) : isComplete;
                    const style = row.source ? FIELD_STYLE[row.source] : undefined;

                    return (
                      <tr
                        className={`vp-table-row vp-table-row--${row.status}${visible ? " vp-table-row--in" : ""}`}
                        key={row.key}
                        style={
                          style
                            ? ({ "--vp-row": style.hex, "--vp-row-soft": style.soft, "--vp-row-tint": style.tint } as CSSProperties)
                            : undefined
                        }
                        ref={(el) => {
                          rowRefs.current[row.key] = el;
                        }}
                      >
                        <td>
                          <span className="vp-trait-name">
                            <span
                              className="vp-row-dot"
                              aria-hidden="true"
                              ref={(el) => {
                                dotRefs.current[row.key] = el;
                              }}
                            />
                            {row.key}
                          </span>
                        </td>
                        <td>
                          <strong>{row.value}</strong>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
